// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2016
//
// ## Meter Description: 6/8 is a ________ meter. (compound ternary)
//
import type { TimeSignatureAlias } from 'shared/definitions/musicTheory/rhythms';
import { timeSignatureAliases } from 'shared/definitions/musicTheory/rhythms';
import type { InteractiveOptions, LayoutSettings, LayoutItem } from './interactivePrototype';
import InteractivePrototype from './interactivePrototype';
import utils from '../utils';
import RadioButtons from './radioButtons';
import config from '../config';
import TimeSignature from '../uTheory.Notation/timeSignature';
import Rhythm from '../uTheory.Notation/rhythm';
import RhythmPanel from './rhythmPanel';
import rejectDuplicates from '../uTheory.Skills/rejectDuplicates';

const blankAnswer = {
  beats: '',
  subdivision: '',
  beatValue: '',
};

interface Layout extends LayoutSettings {
  radios: LayoutItem;
  beatValueInstructions: LayoutItem;
  beatValues: LayoutItem;
}

export interface MeterDescriptionOptions extends InteractiveOptions {
  beats?: boolean;
  /** "This note value gets the beat:" */
  beatValue?: boolean;
}

interface MeterDescriptionQuestion {
  timeSig: TimeSignature;
  beatsPerMeasure: number;
  subdivisionsPerBeat: number;
  beatsString: string;
  beatValue: string;
  subdivisionString: string;
  equals: (b: { timeSig: TimeSignature }) => boolean;
  text: string;
  skills: (string | number)[];
}

export default class MeterDescription extends InteractivePrototype {
  declare layout: Layout;

  beatsButtons!: RadioButtons;

  subdivisionButtons!: RadioButtons;

  rhythmPanel: RhythmPanel | false = false;

  maxQuestions: number | undefined;

  questionText: string | undefined;

  declare questions: MeterDescriptionQuestion[];

  declare currentQuestion: MeterDescriptionQuestion;

  declare options: MeterDescriptionOptions;

  answer: {
    beats: number | string;
    subdivision: string;
    beatValue: string;
  };

  constructor(displayDiv: HTMLDivElement, options: MeterDescriptionOptions = {}) {
    super(displayDiv, options);

    this.settings = {
      instructions: true,
      letters: {
        checkButton: true,
      },
      checkMark: {},
      player: {},
      progressTracker: true,
    };

    this.layout = {
      instructions: { percent: 15, noWrap: true, style: { marginBottom: '0.1em' } },
      letters: { percent: 15 },
      radios: {
        percent: 25,
        visible: true,
      },
      beatValueInstructions: {
        percent: 10,
        visible: !!options.beatValue,
      },
      beatValues: {
        percent: 35,
        visible: !!options.beatValue,
      },
    };

    this.answer = utils.clone(blankAnswer);

    this.init(displayDiv, options);
  }

  init(displayDiv: HTMLDivElement, options: MeterDescriptionOptions) {
    return super.init(displayDiv, options);
  }

  createCustomDivs() {
    this.createRadiosDiv();
    if (this.options.beatValue) this.createBeatValuesDiv();
  }

  createRadiosDiv() {
    const radioDiv = this.createDiv(
      'radioDiv',
      {
        textAlign: 'center',
      },
      true
    );

    const highlighted = config.Colors.highlighted;
    const [subdivisionDiv, beatsDiv] = ['subdivisionDiv', 'beatsDiv'].map((label) =>
      this.createDiv(
        label,
        {
          margin: 'auto',
          display: 'inline-block',
          padding: '1vw',
          width: '',
          verticalAlign: 'top',
        },
        true
      )
    );

    const subdivisionButtons = new RadioButtons(subdivisionDiv as HTMLDivElement, {
      buttons: [
        { text: 'simple', value: 'simple', hotKey: ['s', 'S'] },
        { text: 'compound', value: 'compound', hotKey: ['c', 'C'] },
      ],
      backgroundColor: highlighted,
    });
    const callback = (value: string) => {
      this.answer.subdivision = value;
    };

    subdivisionButtons.addListener(callback);
    const beatsButtons = new RadioButtons(beatsDiv as HTMLDivElement, {
      buttons: [
        { text: 'duple', value: '2', hotKey: ['d', 'D'] },
        { text: 'triple', value: '3', hotKey: ['t', 'T'] },
        { text: 'quadruple', value: '4', hotKey: ['q', 'Q'] },
      ],
      backgroundColor: highlighted,
    });
    beatsButtons.addListener((value: string): void => {
      this.answer.beats = Number(value);
    });

    this.beatsButtons = beatsButtons;
    this.subdivisionButtons = subdivisionButtons;
    const docFragment = document.createDocumentFragment();
    docFragment.appendChild(subdivisionDiv as HTMLDivElement);
    docFragment.appendChild(beatsDiv as HTMLDivElement);
    radioDiv.appendChild(docFragment);

    this.layout.radios.div = radioDiv;
    this.layout.radios.callback = (height: number) =>
      // console.log(`sizing radioDiv to ${ height }`);
      this.sizeTextDiv(height, radioDiv);
    // console.log(`sized radioDiv to ${ height }`);
    this.displayDiv.insertBefore(radioDiv, this.lettersDiv!);
  }

  createBeatValuesDiv() {
    const beatValueInstructionsDiv = this.createDiv('beatValueInstructionsDiv', {
      textAlign: 'center',
      margin: '10px',
      width: '',
    });

    beatValueInstructionsDiv.innerHTML = 'with this note value as the beat:';

    this.layout.beatValueInstructions.div = beatValueInstructionsDiv;
    this.layout.beatValueInstructions.callback = (height: number) =>
      // console.log(`sizing beatValueInstructions to ${ height }`);
      this.sizeTextDiv(height, beatValueInstructionsDiv, { noWrap: true });
    this.displayDiv.insertBefore(beatValueInstructionsDiv, this.lettersDiv!);

    const beatValuesDiv = this.createDiv(
      'beatValuesDiv',
      {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-around',
      },
      true
    );

    // const pushedBGColor = config.Colors.highlighted;

    this.displayDiv.insertBefore(beatValuesDiv, this.lettersDiv!);
    this.rhythmPanel = new RhythmPanel(beatValuesDiv, {
      // pushedBGColor,
      dot: true,
      onChange: (duration) => {
        this.answer.beatValue = duration;
      },
      disableHotKeys: ['s', 'q'],
    });

    this.layout.beatValues.div = beatValuesDiv;
    this.layout.beatValues.callback = (height: number) =>
      this.rhythmPanel ? this.rhythmPanel.resize(height) : 0;
  }

  generateQuestions(questions = this.options.questions) {
    this.questionPrompt = `{question} is a _____ meter${this.options.beatValue ? ',' : '.'}`;
    if (!this.options.drill && !questions) {
      throw new Error(`options.questions must be given in drill mode.`);
    }
    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : questions!
          .map((q) => this.processQuestion(q))
          .filter((q): q is MeterDescriptionQuestion => !!q);
    // console.log('this.questions', this.questions);
    this.maxQuestions = this.questions.length;
  }

  generateDrillQuestions() {
    const questions = timeSignatureAliases
      .map((q) => this.processQuestion(q))
      .filter((q): q is MeterDescriptionQuestion => !!q);
    return rejectDuplicates(questions, this.options.maxQuestions || 6);
  }

  processQuestion(tsString: TimeSignatureAlias): MeterDescriptionQuestion | false {
    const timeSig = new TimeSignature(tsString);
    // If this is mixed meter, we can't have it:
    if (timeSig.beatDurations.length > 1) return false;
    const { beatsPerMeasure, subdivisionsPerBeat } = timeSig;
    const beatsString = ['duple', 'triple', 'quadruple'][beatsPerMeasure - 2];
    const subdivisionString = ['simple', 'compound'][subdivisionsPerBeat - 2];
    const text = timeSig.toCSSString();
    const skills = ['simple_vs_compound', 'dup_trip_quad'];
    if (this.options.beatValue) skills.push('meter_beat_value');

    if (!beatsString || !subdivisionString) return false;

    return {
      timeSig,
      beatsPerMeasure,
      subdivisionsPerBeat,
      beatsString,
      subdivisionString,
      text,
      skills,
      beatValue: timeSig.getBeatDuration().toString(),
      equals: (b: { timeSig: TimeSignature }) => timeSig.equals(b.timeSig),
    };
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel!.addCheckCallback(this.checkAnswer, this);
  }

  newQuestion() {
    super.newQuestion();
    if (this.rhythmPanel) this.rhythmPanel.reset();
    this.answer = utils.clone(blankAnswer);
    this.questionText = this.instructionsDiv!.innerHTML;
    this.beatsButtons.reset();
    this.subdivisionButtons.reset();
  }

  checkAnswer() {
    if (this.rightAnswerDelay) return undefined;
    if (!this.answer.beats) {
      return this.checkMark!.warn('Is it duple, triple or quadruple?');
    }
    if (!this.answer.subdivision) {
      return this.checkMark!.warn('Is it simple or compound?');
    }
    if (this.options.beatValue && this.rhythmPanel && !this.rhythmPanel.rhythmicValueIsChecked) {
      return this.checkMark!.warn('You must select a rhythm first!');
    }

    const correctedAnswer = this.isCorrect();
    const correct = correctedAnswer.correct;

    if (this.options.testMode) return this.checkpointAnswer(true);
    if (correct) {
      return this.rightAnswer(600);
    }

    const hint = this.wrongAnswerCount++ && !this.testMode ? this.hint(correctedAnswer) : undefined;

    // Now let's give them partial credit -- or, only deduct the wrong stuff:
    const skills = [];
    if (!correctedAnswer.subdivisionCorrect) skills.push('simple_vs_compound');
    if (!correctedAnswer.beatsPerMeasCorrect) skills.push('dup_trip_quad');
    if (!correctedAnswer.beatValueCorrect) skills.push('meter_beat_value');

    return this.wrongAnswer(hint, { skills });
  }

  hint({
    subdivisionCorrect,
    beatsPerMeasCorrect,
  }: {
    subdivisionCorrect: boolean;
    beatsPerMeasCorrect: boolean;
  }) {
    const beatsString = ['duple', 'triple', 'quadruple'][Number(this.answer.beats) - 2];

    if (!subdivisionCorrect) {
      return `It's not ${this.answer.subdivision}.`;
    }
    if (!beatsPerMeasCorrect) {
      return `It's not ${beatsString}.`;
    }
    return 'The note value is wrong.';
  }

  /**
   * Returns whether the answer was correct and which of the 3-part answer is correct
   */
  isCorrect() {
    const subdivisionCorrect = this.answer.subdivision === this.currentQuestion.subdivisionString;
    const beatsPerMeasCorrect = this.answer.beats === this.currentQuestion.beatsPerMeasure;
    const beatValueCorrect =
      !this.options.beatValue || this.answer.beatValue === this.currentQuestion.beatValue;

    // console.log('subdivisionCorect', subdivisionCorrect);
    // console.log('beatsPerMeasCorrect', beatsPerMeasCorrect);
    // console.log('beatValueCorrect', beatValueCorrect);

    const correct = subdivisionCorrect && beatsPerMeasCorrect && beatValueCorrect;
    return { subdivisionCorrect, beatsPerMeasCorrect, beatValueCorrect, correct };
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString: string) {
    return this.loadingPromise.then(() => {
      this.generateQuestions([questionString]);
      this.newQuestion();
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData() {
    const correct = this.isCorrect().correct;
    const q = this.currentQuestion;
    const beatsString = this.answer.beats
      ? ['duple', 'triple', 'quadruple'][Number(this.answer.beats) - 2]
      : false;

    let studentAnswerText = `${this.answer.subdivision || 'unanswered'} ${
      beatsString || 'unanswered'
    }`;
    let correctAnswerText = `${q.subdivisionString} ${q.beatsString}`;
    if (this.options.beatValue) {
      const answerBeatValue =
        this.answer.beatValue && this.rhythmPanel && this.rhythmPanel.rhythmicValueIsChecked
          ? new Rhythm(this.answer.beatValue).toRhythmFontString()
          : 'beat value unanswered';
      studentAnswerText += `, ${answerBeatValue}`;
      correctAnswerText += `, ${new Rhythm(q.beatValue).toRhythmFontString()}`;
    }

    const answerAndData = {
      studentAnswerText,
      correctAnswerText,
      question: this.currentQuestion.timeSig.toString(),
      questionType: 'MeterDescription',
      questionText: this.questionText || 'undefined',
      questionSVG: undefined,
      studentAnswerSVG: undefined,
      rightAnswers: correct ? 1 : 0,
      wrongAnswers: correct ? 0 : 1,
      skills: q.skills,
    };
    return answerAndData;
  }

  static registry = {
    description: 'Simple vs. Compound / Duple vs. Triple vs. Quad',
    skills: ['simple_vs_compound', 'dup_trip_quad', 'meter_beat_value'],
    options: {
      beatValue: true,
    },
  };
}
